import React, { useState, useEffect } from 'react';
import { securePut, secureGet } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useHistory } from 'react-router-dom';
import { formatAmount } from "../../utils/format";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useLocation } from 'react-router-dom';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingLogo from '../../components/LoadingLogo';

import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import plusY from '../../icons/plusY.svg';
import minusY from '../../icons/minusY.svg';
import noImage from '../../icons/noImageIcon.svg';

const OrderForm = ({
    title,
    baseFields = [],
    listFields = [],
    additionalFields = [],
    products = [],
    onSuccess,
}) => {
    const [formValues, setFormValues] = useState({});
    const [listEntities, setListEntities] = useState([]);
    const [currentEntity, setCurrentEntity] = useState({});
    const [totalHT, setTotalHT] = useState(0);
    const [totalTTC, setTotalTTC] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const history = useHistory();
    const [currentEncoursRestant, setCurrentEncoursRestant] = useState();
    const [originalEncoursRestant, setOriginalEncoursRestant] = useState(0);
    const [productAvailabilities, setProductAvailabilities] = useState({}); const { t } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const [order, setOrder] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [loading, setLoading] = useState(true);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    useEffect(() => {
        const checkDataReadiness = () => {
            const baseFieldsReady = baseFields.every(field =>
                !field.options || field.options.length > 0
            );

            if (id) {
                return order && baseFieldsReady;
            }
            return baseFieldsReady;
        };

        if (checkDataReadiness()) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [order, baseFields, id]);


    useEffect(() => {
        const abortController = new AbortController();
        const fetchOrder = async () => {
            if (!id) {
                console.warn("No ID provided in the URL parameters.");
                return;
            }
            try {
                const response = await secureGet(`/carts/${id}`, showSnackbar, { signal: abortController.signal });
                const fetchedOrder = response.data;
                setOrder(fetchedOrder);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Failed to fetch order:", error);
                }
            }
        };

        fetchOrder();
        return () => {
            abortController.abort();
        };
    }, [id]);

    useEffect(() => {
        if (order) {
            const abortController = new AbortController();
            const initializeEncoursCalculation = async () => {
                try {
                    const clientId = order.client?.id;
                    if (!clientId) return;

                    const financialsResponse = await secureGet(`/clients/${clientId}/financials`, showSnackbar, { signal: abortController.signal });
                    const financials = financialsResponse.data;

                    const encours = financials.encours;
                    const nonRegle = financials.nonRegle;
                    const impayes = financials.impayes;
                    const totalEncours = encours + nonRegle + impayes;
                    const maxEncours = order.client?.encoursMaximum;
                    const initialRemainingEncours = maxEncours - totalEncours;


                    setOriginalEncoursRestant(initialRemainingEncours);
                    setCurrentEncoursRestant(initialRemainingEncours);

                    if (initialRemainingEncours < 0) {
                        setErrorMessage(t("placeholder.encoursError"));
                    }
                } catch (error) {
                    console.error("Error while fetching client financials in modify mode:", error);
                    setOriginalEncoursRestant(0);
                    setCurrentEncoursRestant(0);
                }
            };

            initializeEncoursCalculation();
            
            return () => {
                abortController.abort();
            };
        }
    }, [order, t]);


    useEffect(() => {
        if (order) {

            const initialValues = {
                clientId: order.client?.id,
                paymentMode: order.paymentMode ?? "",
                repoId: order.repo?.id || "",
                comment: order.comment || "",
                additionalFields: order.fields || {},
            };
            setFormValues(initialValues);


            const productList = order.details.map((detail) => ({
                product: detail.product,
                quantity: detail.quantity,
                discount: detail.salesDiscount || 0,
            }));
            setListEntities(productList);
        }
    }, [order]);

    useEffect(() => {
        const calculatedTotalHT = listEntities.reduce(
            (sum, entity) =>
                sum +
                entity.quantity * (entity.product.priceHT * (1 - entity.discount / 100)),
            0
        );

        const calculatedTotalTTC = listEntities.reduce(
            (sum, entity) =>
                sum +
                entity.quantity * (entity.product.priceTTC * (1 - entity.discount / 100)),
            0
        );

        if (currentEncoursRestant < totalTTC) {
            setErrorMessage(t("placeholder.encoursError"));
        }
        setTotalHT(calculatedTotalHT.toFixed(2));
        setTotalTTC(calculatedTotalTTC.toFixed(2));

    }, [listEntities, originalEncoursRestant, formValues.clientId, currentEncoursRestant, totalTTC, t]);

    useEffect(() => {
        const fetchAvailabilities = async () => {
            const repoId = formValues.repoId;
            if (!repoId) return;

            const productIds = listEntities
                .map(entity => entity.product?.id)
                .filter(Boolean);
            const uniqueProductIds = [...new Set(productIds)];

            const updates = {};

            for (const productId of uniqueProductIds) {
                try {
                    const response = await secureGet(`/products/${productId}/availability`, showSnackbar);
                    const availability = response.data.availability.find(
                        item => item.repoId === repoId
                    );
                    updates[productId] = availability ? availability.quantity : 0;
                } catch (error) {
                    console.error("Error fetching availability:", error);
                    updates[productId] = 0;
                }
            }

            setProductAvailabilities(prev => {
                const newState = { ...prev };
                Object.entries(updates).forEach(([productId, quantity]) => {
                    newState[productId] = {
                        ...newState[productId],
                        [repoId]: quantity
                    };
                });
                return newState;
            });
        };

        fetchAvailabilities();
    }, [formValues.repoId, listEntities]);

    const handleInputChange = async (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));

        const selectedClient = baseFields
            .find((field) => field.name === "clientId")
            ?.options.find((option) => option.value === Number(value));

        if (name === "clientId" && value) {
            try {
                const financialsResponse = await secureGet(`/clients/${value}/financials`, showSnackbar);
                const financials = financialsResponse.data;

                const encours = financials.encours || 0;
                const nonRegle = financials.nonRegle || 0;
                const impayes = financials.impayes || 0;

                const totalEncours = encours + nonRegle + impayes;
                const maxEncours = selectedClient.encoursMax;
                const initialRemainingEncours = maxEncours - totalEncours;
                setOriginalEncoursRestant(initialRemainingEncours);
                setCurrentEncoursRestant(initialRemainingEncours);

                if (initialRemainingEncours < 0) {
                    setErrorMessage(t("placeholder.encoursError"));
                } else {
                    setErrorMessage("");
                }
            } catch (error) {
                console.error("Error while fetching client financials:", error);
                setOriginalEncoursRestant(0);
                setCurrentEncoursRestant(0);
            }
        }
    };


    const removeListEntity = (index) => {
        setListEntities((prevEntities) => {
            const removedEntity = prevEntities[index];
            const removedEntityTTC =
                removedEntity.quantity *
                removedEntity.product.priceTTC *
                (1 - removedEntity.discount / 100);


            const updatedEntities = prevEntities.filter((_, i) => i !== index);

            return updatedEntities;
        });
    };

    const handleQuantityChange = (index, newQuantity) => {
        setListEntities((prevEntities) => {
            const updatedEntities = [...prevEntities];
            const entity = updatedEntities[index];

            const oldEntityTTC =
                entity.quantity *
                entity.product.priceTTC *
                (1 - entity.discount / 100);
            const newEntityTTC =
                newQuantity *
                entity.product.priceTTC *
                (1 - entity.discount / 100);

            entity.quantity = Math.max(0, newQuantity);

            const updatedTotalTTC = totalTTC - oldEntityTTC + newEntityTTC;
            setTotalTTC(updatedTotalTTC.toFixed(2));

            return updatedEntities;
        });
    };

    const handleDiscountChange = (index, change) => {
        setListEntities((prevEntities) => {
            return prevEntities.map((entity, i) => {
                if (i === index) {
                    const oldEntityTTC = entity.quantity * entity.product.priceTTC * (1 - entity.discount / 100);

                    const newDiscount = Math.max(0, parseFloat((entity.discount + change).toFixed(1)));


                    const newEntity = { ...entity, discount: newDiscount };

                    const newEntityTTC = newEntity.quantity * newEntity.product.priceTTC * (1 - newDiscount / 100);


                    return newEntity;
                }
                return entity;
            });
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const fieldsToValidate = listEntities.length > 0 
            ? [...baseFields, ...additionalFields] 
            : [...baseFields, ...listFields, ...additionalFields];
            
        const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
        if (missing.length > 0) {
            showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
            return;
        }


        const payload = {
            ...(order?.id && { cartId: order.id }),
            clientId: formValues.clientId,
            comment: formValues.comment,
            details: listEntities.map((entity) => ({
                productId: entity.product?.id,
                quantity: entity.quantity,
                salesDiscount: entity.discount,
            })),
            fields: formValues.additionalFields || {},
            paymentMode: formValues.paymentMode,
            repoId: formValues.repoId,
        };

        if (payload.details.length === 0) {
            showSnackbar(t("placeholder.orderError"), "error");
            return;
        }


        try {
            const response = await securePut("/carts/add", payload, showSnackbar);

            onSuccess();
            history.push({
                pathname: "/orders",
                state: {
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });
            setFormValues({});
            setListEntities([]);
        } catch (error) {
            console.error("Failed to submit order:", error);
        }
    };


    const addListEntity = () => {
        const { product, quantity } = currentEntity;

        if (!product || !quantity || quantity <= 0) {
            showSnackbar(t('placeholder.empty_fields'), "error");
            return;
        }

        const selectedProduct = products.find((p) => p.value === product);

        const newEntity = {
            product: selectedProduct.fullEntity,
            quantity: parseInt(quantity, 10),
            discount: 0,
        };

        const newEntityTTC = newEntity.quantity * selectedProduct.fullEntity.priceTTC;


        setListEntities((prevEntities) => {
            const updatedEntities = [...prevEntities, newEntity];
            const updatedTotalTTC = updatedEntities.reduce(
                (sum, entity) =>
                    sum + entity.quantity * entity.product.priceTTC * (1 - entity.discount / 100),
                0
            );

            setTotalTTC(updatedTotalTTC);
            return updatedEntities;
        });

        setCurrentEntity({});
    };

    const handleCancel = () => {
        if (location.pathname.includes("details")) {
            history.push({
                pathname: `/orders/details/${order.id}`,

            });
        } else {
            history.push({
                pathname: "/orders",
                state: {
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });

        }
    };

    const handleImageError = (e) => {
        e.target.src = noImage;
        e.target.style.width = "25px";
        e.target.style.height = "25px";
        e.target.style.objectFit = "contain";
    };


    return (
        <div className="   ">
            <Helmet>
                <title>GrayFox - {t("titles.orders")}</title>
            </Helmet>
            <Breadcrumbs />

            <h2 className="text-[32px] text-[#38485C] font-semibold mb-4">{title}</h2>
            {loading ? (
                <div className="flex justify-center items-center h-[300px]">
                    <LoadingLogo />
                </div>
            ) : (
                <form className="space-y-6">
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {baseFields.map((field, index) => (
                                <div key={index}>
                                    <Input
                                        type={field.type || "text"}
                                        label={field.label}
                                        name={field.name}
                                        value={
                                            formValues[field.name] || ""
                                        }
                                        options={field.options || []}
                                        required={field.required}
                                        onChange={(value) => handleInputChange(field.name, value)}
                                    />

                                </div>
                            ))}
                        </div>
                        {errorMessage && (
                            <p className=" text-sm text-red-500 font-semibold">{errorMessage}</p>
                        )}
                    </div>


                    {/* Additional Fields */}
                    {additionalFields.length > 0 && (
                        <div className="space-y-4">
                            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.additional`)}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {additionalFields.map((field, index) => (
                                    <Input
                                        key={index}
                                        label={field.label}
                                        name={field.name}
                                        type={field.type || "text"}
                                        value={formValues[field.name] || ""}
                                        required={field.required}
                                        onChange={(value) => handleInputChange(field.name, value)}
                                        options={field.options || []}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {listFields.length > 0 && (
                        <div className="space-y-4">
                            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.products`)}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {listFields.map((field, index) => (
                                    <Input
                                        key={index}
                                        type={field.type || 'text'}
                                        label={field.label}
                                        name={field.name}
                                        value={currentEntity[field.name] || ''}
                                        onChange={(value) => setCurrentEntity((prevState) => ({ ...prevState, [field.name]: value }))}
                                        required={field.required}
                                        options={field.name === 'product'
                                            ? products.map((product) => ({
                                                value: product.value,
                                                name: product.name,
                                                reference: product.reference,
                                                fullEntity: product.fullEntity,
                                            }))
                                            : field.options || []}
                                        disabled={!formValues.clientId}

                                    />
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addListEntity()}
                                    className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
                                >
                                    <img src={plusIcon} alt="Add" className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {formValues.clientId && (
                        <div className=" text-sm text-blue-600 font-semibold">
                            {t(`rows.remainingEncours`)}: {formatAmount(currentEncoursRestant)}
                        </div>
                    )}

                    {/* List Entity List */}
                    {listEntities.length > 0 && (
                        <div className="space-y-[8px] mt-6">
                            {listEntities.map((entity, index) => {
                                const discountMultiplier = 1 - (entity.discount || 0) / 100;
                                const discountedPriceHT =
                                    entity.discount > 0
                                        ? (entity.product.priceHT * discountMultiplier).toFixed(2)
                                        : entity.product.priceHT?.toFixed(2);
                                const discountedPriceTTC =
                                    entity.discount > 0
                                        ? (entity.product.priceTTC * discountMultiplier).toFixed(2)
                                        : entity.product.priceTTC?.toFixed(2);

                                return (
                                    <div
                                        key={index}
                                        className="grid grid-cols-[20%_12%_12%_16%_12%_12%_8%_8%] items-center py-2 px-4 rounded-lg bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-0"
                                    >
                                        {/* Product Info */}
                                        <div className="flex items-center gap-2">
                                            <div className="w-[76px] h-[76px] flex items-center justify-center  shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-[12px] overflow-hidden">
                                                {entity.product?.image ? (
                                                    <img
                                                        src={entity.product.image}
                                                        alt={entity.product.name}
                                                        className="w-full h-full object-contain"
                                                        onError={handleImageError}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-white"></div>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-[#043F88] font-semibold text-[12px]">
                                                    {entity.product?.reference || "N/A"}
                                                </p>
                                                <p className="text-[#6B78F8] font-semibold text-[16px]">
                                                    {entity.product?.name || "Produit"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Prix HT */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuHT`).toUpperCase()}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(discountedPriceHT)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.product?.priceHT)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Prix TTC */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuTTC`).toUpperCase()}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(discountedPriceTTC)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.product?.priceTTC)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Remise */}
                                        <div className="flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.remise%`)}</p>
                                            <div className="flex items-stretch rounded-[12px] border border-[#fea26f] bg-[#fffaf8] divide-x divide-[#fea26f]">
                                                <button
                                                    type="button"
                                                    onClick={() => handleDiscountChange(index, -0.1)}
                                                    className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                >
                                                    <img src={minusY} alt="Minus" className="w-4 h-4" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center text-[#38485C] font-semibold text-[14px]">
                                                    {entity.discount?.toFixed(1) || "0.0"}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDiscountChange(index, 0.1)}
                                                    className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                >
                                                    <img src={plusY} alt="Plus" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Quantité */}
                                        <div className="flex flex-col items-center">
                                            <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.quantity`).toUpperCase()}</p>
                                            <div className="flex items-stretch rounded-[12px] border border-[#6C78F8] bg-[#6B78F858] divide-x divide-[#6C78F8]">
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, Math.max(entity.quantity - 1, 1))}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                >
                                                    <img src={minus} alt="Minus" className="w-4 h-4" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center font-semibold text-[#38485C] text-[14px]">
                                                    {entity.quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, entity.quantity + 1)}
                                                    className="w-8 h-8 flex items-center justify-center"
                                                    disabled={entity.quantity >= productAvailabilities[entity.product.id]?.[formValues.repoId]}
                                                >
                                                    <img src={plus} alt="Plus" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Disponibilité */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">
                                                {t(`rows.dispo`)}
                                            </p>
                                            <p className="text-[#043F88] font-medium text-[20px]">
                                                {formValues.repoId
                                                    ? productAvailabilities[entity.product.id]?.[formValues.repoId] || 0
                                                    : 0}
                                            </p>
                                        </div>


                                        {/* Prix Total */}
                                        <div className="text-center">
                                            <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.totalPrice`)}</p>
                                            <p className="text-[#043F88] font-bold text-lg">
                                                {formatAmount(entity.quantity * discountedPriceTTC)}
                                            </p>
                                            {entity.discount > 0 && (
                                                <p className="text-[#ACAFC6] text-sm line-through">
                                                    {formatAmount(entity.quantity * entity.product?.priceTTC)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Delete Icon */}
                                        <div className="flex justify-center">
                                            <button
                                                type="button"
                                                onClick={() => removeListEntity(index)}
                                                className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                            >
                                                <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Totals and Footer */}
                    <div className="flex justify-between items-center mt-6">
                        <div className="flex text-left">
                            <p className="text-[#231E29] text-[16px] font-medium">
                                TOTAL HT:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalHT)}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL TTC:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalTTC)}
                            </p>
                        </div>
                        <div className="space-x-4">
                            <SaveCancel
                                onSaveClick={(e) => handleSubmit(e)}
                                onCancelClick={handleCancel}
                            />
                        </div>
                    </div>

                </form>

            )}
            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />
        </div>
    );

};

export default OrderForm;
